/**
 * Signup attribution — first-party record of where a visitor came from.
 *
 * Every page load with an external referrer or a campaign/click-id param is a
 * "touch". The first touch is kept for 90 days and never overwritten; the last
 * non-direct touch is overwritten on each new one. Both are stored in a
 * cookie scoped to .event-flow.co so the marketing site (www) and the app
 * (app.) share it, and are attached to the Supabase user at signup
 * (user_metadata.signup_attribution). Unlike GA4, this survives ad/tracker
 * blockers because nothing is sent to a third party.
 *
 * The marketing site (breichbilt-hq marketing/sites/eventflow, Head.astro)
 * runs an inline copy of recordTouch — keep the cookie name, fields, and
 * rules identical in both places.
 */

export const ATTRIBUTION_COOKIE = 'ef_attr';
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const MAX_URL_LENGTH = 250;

export const TRACKED_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id',
  'gclid', 'gbraid', 'wbraid', 'dclid', 'fbclid', 'msclkid', 'li_fat_id', 'ttclid', 'twclid',
];

function cookieDomain(host) {
  return host === 'event-flow.co' || host.endsWith('.event-flow.co') ? '; domain=.event-flow.co' : '';
}

function isInternalHost(refHost, host) {
  return refHost === host || refHost === 'event-flow.co' || refHost.endsWith('.event-flow.co');
}

/**
 * Build a touch from a page URL + referrer, or null for a direct/internal
 * visit with no tracked params.
 */
export function buildTouch(href, referrer, now = new Date()) {
  const url = new URL(href);
  const touch = { ts: now.toISOString(), landing: (url.pathname + url.search).slice(0, MAX_URL_LENGTH) };
  let hasSignal = false;

  for (const key of TRACKED_PARAMS) {
    const value = url.searchParams.get(key);
    if (value) {
      touch[key] = value.slice(0, MAX_URL_LENGTH);
      hasSignal = true;
    }
  }

  if (referrer) {
    try {
      const ref = new URL(referrer);
      if (!isInternalHost(ref.hostname, url.hostname)) {
        touch.referrer = referrer.slice(0, MAX_URL_LENGTH);
        touch.referrer_host = ref.hostname;
        hasSignal = true;
      }
    } catch {
      // Malformed referrer — ignore it.
    }
  }

  return hasSignal ? touch : null;
}

export function readAttribution() {
  const match = document.cookie.match(new RegExp(`(?:^|; )${ATTRIBUTION_COOKIE}=([^;]*)`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

/** Record this page load as a touch. Call once at startup. */
export function recordTouch() {
  try {
    const touch = buildTouch(window.location.href, document.referrer);
    const current = readAttribution() || {};
    if (current.first && !touch) return;

    const next = {
      first: current.first || touch || { ts: new Date().toISOString(), landing: window.location.pathname, direct: true },
      last: touch || current.last,
    };
    document.cookie = `${ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(next))}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax; Secure${cookieDomain(window.location.hostname)}`;
  } catch (err) {
    console.warn('[Attribution] recordTouch failed:', err);
  }
}

/** GA4 client id from the _ga cookie ("GA1.1.123.456" -> "123.456"), shared across event-flow.co subdomains. */
function gaClientId() {
  const match = document.cookie.match(/(?:^|; )_ga=GA\d\.\d\.([^;]+)/);
  return match ? match[1] : null;
}

/** Payload stored on the Supabase user at signup. */
export function signupAttribution() {
  const attr = readAttribution() || {};
  return {
    first: attr.first || null,
    last: attr.last || null,
    ga_client_id: gaClientId(),
    signup_page: window.location.pathname,
    captured_at: new Date().toISOString(),
  };
}
